import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  getTimeRangeDay,
  getTimeRange12Hours,
  getTimeRangeWeek,
  getTimeRangeMonth,
  getTimeRangeYear,
} from "../../../../functions/dates";
import TruPakDashboardController from "../../TruPakDashboard/TruPakDashboardController";
import PouchesPerHourGraph from "../graphs/PouchesPerHourGraph";
import { ScreenContext } from "../../../../contexts/ScreenContext";
import { useMediaQuery } from "react-responsive";
import styles from "../TruPakDashboard.module.css";
import stylesMobile from "../MobileViews/TruPakDashboardMobileView.module.css";
import { useTranslation } from "react-i18next";

const GetPouchesPerHourGraph = ({
  user,
  selectedDevices,
  selectedSites,
  loader,
}) => {
  const { t } = useTranslation("trupakDashboard");

  const [timeRange, setTimeRange] = useState("month");
  const [startDate, setStartDate] = useState(null);
  const [stopDate, setStopDate] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [benchmarkValue, setBenchmarkValue] = useState(0);
  const [fetchingData, setFetchingData] = useState(false);

  const _controllerRef = useRef(TruPakDashboardController);

  const { smallScreen } = useContext(ScreenContext);
  const smScreen = useMediaQuery(smallScreen);

  useEffect(() => {
    let dateRangeObject;
    if (timeRange === "year") {
      dateRangeObject = getTimeRangeYear();
    } else if (timeRange === "12 hours") {
      dateRangeObject = getTimeRange12Hours();
    } else if (timeRange === "day") {
      dateRangeObject = getTimeRangeDay();
    } else if (timeRange === "week") {
      dateRangeObject = getTimeRangeWeek();
    } else {
      dateRangeObject = getTimeRangeMonth();
    }
    setStartDate(dateRangeObject.startDate);
    setStopDate(dateRangeObject.stopDate);
  }, [timeRange]);

  const handleBenchmarkData = useCallback(() => {
    let tempBenchmarkValue = 0;
    selectedDevices.forEach((device) => {
      if (
        device?.device &&
        device.device?.settings &&
        device.device.settings?.pouchesPerHourBenchmark
      ) {
        tempBenchmarkValue += Number(
          device.device.settings.pouchesPerHourBenchmark
        );
      }
    });
    setBenchmarkValue(tempBenchmarkValue);
  }, [selectedDevices]);

  useEffect(() => {
    if (startDate && stopDate && selectedDevices.length) {
      const utcStartDate = startDate.toUTCString();
      const utcStopDate = stopDate.toUTCString();

      async function updateGraphs() {
        setGraphData([]);
        setFetchingData(true);
        let hourCountData = [];
        let resultsFound = false;

        for (let i = 0; i < selectedDevices.length; i++) {
          const device = selectedDevices[i];
          const site = selectedDevices[i].site;

          await _controllerRef.current
            .getTruPakPouchesPerHourData(
              user,
              site,
              device,
              utcStartDate,
              utcStopDate
            )
            .then((data) => {
              const getScriptsForHour = (hour, results) => {
                return results.filter((o) => {
                  const dt = new Date(o.packagedOn); // UTC
                  return dt.getHours() === hour; // Local time
                });
              };

              let forIndex = 0;
              for (let hour = 0; hour < 24; hour++) {
                const temp = getScriptsForHour(
                  hour,
                  data.results.truPakPackagedPouchesData
                );

                if (hourCountData.length <= 23) {
                  hourCountData.push({
                    hour: hour,
                    count: temp.length,
                  });
                } else {
                  hourCountData[forIndex].count += temp.length;
                }
                forIndex++;
              }
              if (data?.results?.truPakPackagedPouchesData?.length) {
                resultsFound = true;
              }
            });
        } // end for statement
        if (resultsFound) {
          setGraphData(
            hourCountData.map((result, index) => ({
              x:
                index < 12
                  ? `${result.hour > 0 ? result.hour : 12} AM`
                  : `${result.hour > 12 ? result.hour - 12 : 12} PM`,
              y: result.count,
            }))
          );
        } else {
          setGraphData([]);
        }
        handleBenchmarkData();
        setFetchingData(false);
      }
      if (selectedDevices.length) {
        updateGraphs();
      }
    }
  }, [
    selectedSites,
    startDate,
    stopDate,
    selectedDevices,
    user,
    handleBenchmarkData,
  ]);

  const handleTimeRangePouchesPerHour = (stringVal) => {
    setTimeRange(stringVal);
  };

  if (selectedDevices.length) {
    return (
      <div
        className={
          smScreen
            ? `${stylesMobile.TruPakDashboard__graphContainerMobile} ${stylesMobile["TruPakDashboard__graphContainerMobile--paddingBottom"]}`
            : `${styles.TruPakDashboard__graphContainerMobile} ${styles["TruPakDashboard__graphContainer--paddingBottom"]}`
        }
        // onClick={!fetchingData ? () => handleGraphClick(1) : null}
      >
        {fetchingData && loader}
        <PouchesPerHourGraph
          user={user}
          fetchingData={fetchingData}
          graphData={graphData}
          benchmarkValue={benchmarkValue}
          graphTitle={t("pouchesTitle")}
          palette={["#318ce7", "#E68000"]}
          timeRange={timeRange}
          onTimeRange={handleTimeRangePouchesPerHour}
          primarySeriesLabel={t("pouchesDispensedLabel")}
          secondarySeriesLabel={t("pouchesBenchmarkLabel")}
        />
      </div>
    );
  } else {
    return null;
  }
};

export default GetPouchesPerHourGraph;
